import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Button } from '@components';
import { CallRoute } from '../types';

interface CallRoutesTableProps {
  routes: CallRoute[];
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onCreateRoute?: () => void;
}

export const CallRoutesTable: React.FC<CallRoutesTableProps> = ({
  routes,
  onEdit,
  onDelete,
  onCreateRoute,
}) => {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent>
        <div className="overflow-x-auto w-full">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('common.name')}
                </th>
                <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('callRouting.phoneNumber')}
                </th>
                <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('callRouting.businessHours')}
                </th>
                <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('common.status')}
                </th>
                <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('callRouting.lastUpdated')}
                </th>
                <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {routes.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12">
                    <div className="text-center space-y-4">
                      <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                        <span className="material-icons text-2xl text-gray-400 dark:text-gray-500">
                          route
                        </span>
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {t('callRouting.noRoutesFound')}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                          {t('callRouting.noRoutesFoundDescription')}
                        </p>
                      </div>
                      <div className="pt-2">
                        <Button
                          variant="gradient-multi"
                          size="sm"
                          onClick={onCreateRoute}
                          className="inline-flex items-center gap-2"
                        >
                          <span className="material-icons text-sm">add</span>
                          {t('callRouting.createCallRoute')}
                        </Button>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                routes.map(route => (
                  <tr
                    key={route.id}
                    className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  >
                    <td className="py-2 px-3">
                      <div>
                        <div className="font-semibold text-gray-900 dark:text-white">
                          {route.name}
                        </div>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-sm text-gray-600 dark:text-gray-400">
                      {route.phone_number}
                    </td>
                    <td className="py-2 px-3 text-sm text-gray-600 dark:text-gray-400">
                      {route.business_hours}
                    </td>
                    <td className="py-2 px-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          route.status === 'active'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                        }`}
                      >
                        {route.status
                          .split(' ')
                          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                          .join(' ')}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-sm text-gray-600 dark:text-gray-400">
                      {new Date(route.modified).toLocaleDateString()}
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEdit(route.id)}
                          className="p-1 h-8 w-8"
                          title="Edit Route"
                        >
                          <span className="material-icons text-lg">edit</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDelete(route.id)}
                          className="p-1 h-8 w-8"
                          title="Delete Route"
                        >
                          <span className="material-icons text-red-600 text-lg">delete</span>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
